import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Stack, TextField, Alert,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { admissionApplicationService } from '../../../services/admissions/admissionApplicationService';
import { AdmissionApplication } from '../../../types/admissions';

interface Props {
  open: boolean;
  application: AdmissionApplication;
  onClose: () => void;
  onDone: () => Promise<void> | void;
}

/**
 * Withdraw dialog — reason-gated. The backend records a withdrawal entry on
 * the application (`withdrawal` subdoc) and runs the reach-anywhere transition
 * to `Withdrawn`. Refund computation (per cycle.refundPolicy) lands in the
 * next FE chunk with the Fees tab; for Phase 1 the finance team runs the
 * refund via the Fee module.
 */

const WithdrawDialog: React.FC<Props> = ({ open, application, onClose, onDone }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const close = () => {
    if (busy) return;
    setReason('');
    onClose();
  };

  const submit = async () => {
    const trimmed = reason.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      await admissionApplicationService.withdraw(application._id, trimmed);
      enqueueSnackbar('Application withdrawn', { variant: 'success' });
      await onDone();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to withdraw application', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle component="div">
        <Typography variant="h6" fontWeight={700}>Withdraw application</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            This records the parent's cancellation on <b>{application.name}</b>'s application. The
            application moves to <b>Withdrawn</b> (terminal).
          </Typography>
          <TextField
            label="Reason"
            placeholder="E.g. family relocating; parent chose another school"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            required
            autoFocus
          />
          <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Unpaid challans are cancelled automatically. Any fee already collected is flagged on
              the timeline so Accounts can settle the refund from Fees.
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={close} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          color="warning"
          disableElevation
          onClick={submit}
          disabled={busy || !reason.trim()}
        >
          {busy ? 'Withdrawing…' : 'Withdraw application'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WithdrawDialog;
